/**
 * @file interpret/engine/s2sSocket.ts
 * @brief The interpreter WebSocket client: web twin of mobile `useTurnInterpreter`.
 *
 * @details
 * Same wire protocol as the phone's Silero screen
 * (`docs/architecture/websocket-protocol.md`):
 * - out: `start` (auto-lid, 16 kHz, `clientVad:false`), `audio` (base64 pcm16),
 *   `vad end manual` (the noisy-room "end turn" backup), `pause`, `resume`,
 *   `stop`, and `ping` every 20 s (browsers cannot send protocol pings; the
 *   server answers `pong`).
 * - in: `status`, `final`, `detected_speaker`, `noise_warning`, `error`.
 *
 * A plain class, not a hook. The phone's hook returns a new object every render,
 * which has bitten effect dependencies more than once; a class instance is stable
 * and can be tested with a fake socket (`s2sSocket.test.ts`).
 *
 * Behaviour carried over from the phone, on purpose:
 * - Reconnect up to 5 times, 1.5 s apart, re-sending `start` (and `pause` if the
 *   user had paused) on every open.
 * - Audio produced while disconnected is DROPPED, not buffered: the server runs
 *   Silero VAD on a continuous stream, and flushing stale audio into a fresh
 *   session makes phantom turns (`dropBufferedAudioOnReconnect`).
 * New on web:
 * - An `error` with `recoverable:false` (e.g. `session_not_found` from the
 *   ownership check) ends the socket for good instead of reconnecting into the
 *   same refusal five times.
 *
 * @author DES
 * @date 01-10-2026
 */

export type SocketLike = {
  readyState: number;
  send(data: string): void;
  close(): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onclose: ((ev: unknown) => void) | null;
};

export type S2sFinal = {
  asr: string;
  mt: string;
  lid?: string;
  speaker?: string;
  audioUrl?: string | null;
  audioB64?: string | null;
  audioMime?: string | null;
  objectName?: string | null;
  playbackSec?: number;
  /** Server time from end of speech to `final`, for the RTT pill. */
  e2eServerMs?: number;
};

export type S2sSocketError = Error & { code?: string; recoverable: boolean };

export type S2sConnection = "idle" | "connecting" | "connected" | "reconnecting" | "closed";

export type S2sSocketEvents = {
  onStatus?: (status: string) => void;
  onFinal?: (final: S2sFinal) => void;
  onDetectedSpeaker?: (speaker: "A" | "B") => void;
  onNoiseWarning?: (show: boolean) => void;
  onError?: (err: S2sSocketError) => void;
  onConnection?: (state: S2sConnection) => void;
};

export type S2sSocketOptions = {
  /** Called on every (re)connect so a refreshed token is picked up. */
  url: () => string;
  fromLang: string;
  toLang: string;
  events?: S2sSocketEvents;
  createSocket?: (url: string) => SocketLike;
  maxReconnectAttempts?: number;
  reconnectDelayMs?: number;
  pingIntervalMs?: number;
  connectTimeoutMs?: number;
};

const OPEN = 1;

function e2eOf(timing: unknown): number | undefined {
  const v = (timing as { e2eServerMs?: unknown } | null)?.e2eServerMs;
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

function socketError(message: string, code?: string, recoverable = true): S2sSocketError {
  return Object.assign(new Error(message), { code, recoverable });
}

export class S2sSocket {
  private readonly opts: Required<Omit<S2sSocketOptions, "events">> & {
    events: S2sSocketEvents;
  };
  private ws: SocketLike | null = null;
  private sessionId = "";
  private paused = false;
  private closing = false;
  private attempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private _state: S2sConnection = "idle";

  constructor(options: S2sSocketOptions) {
    this.opts = {
      createSocket: (url) => new WebSocket(url) as unknown as SocketLike,
      maxReconnectAttempts: 5,
      reconnectDelayMs: 1500,
      pingIntervalMs: 20_000,
      connectTimeoutMs: 15_000,
      ...options,
      events: options.events ?? {},
    };
  }

  get state(): S2sConnection {
    return this._state;
  }

  /** Open the socket for a session created by `/api/s2s/start`. Resolves once `start` is sent. */
  connect(sessionId: string): Promise<void> {
    this.sessionId = sessionId;
    this.closing = false;
    this.attempts = 0;
    this.setState("connecting");
    return this.open(true);
  }

  /** One 20 ms pcm16 frame. Dropped unless connected and not paused. */
  sendAudio(base64: string, samples: number): void {
    if (this.paused || !this.isOpen()) return;
    this.send({ type: "audio", data: base64, samples, encoding: "pcm16" });
  }

  /** Finish the current turn now (server VAD honours `manual:true`). */
  endTurn(): void {
    if (this.paused) return;
    this.send({ type: "vad", event: "end", manual: true, clientTs: Date.now() });
  }

  pause(): void {
    this.paused = true;
    this.send({ type: "pause" });
  }

  resume(): void {
    this.paused = false;
    this.send({ type: "resume" });
  }

  stop(): void {
    this.closing = true;
    this.clearTimers();
    if (this.isOpen()) this.send({ type: "stop" });
    try {
      this.ws?.close();
    } catch {
      /* already closed */
    }
    this.ws = null;
    this.paused = false;
    this.setState("closed");
  }

  private isOpen(): boolean {
    return !!this.ws && this.ws.readyState === OPEN;
  }

  private send(obj: object): void {
    if (!this.isOpen()) return;
    try {
      this.ws?.send(JSON.stringify(obj));
    } catch {
      /* the close handler deals with a dead socket */
    }
  }

  private setState(next: S2sConnection): void {
    if (this._state === next) return;
    this._state = next;
    this.opts.events.onConnection?.(next);
  }

  private clearTimers(): void {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.reconnectTimer = null;
    this.pingTimer = null;
  }

  private open(initial: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const settle = (fn: () => void) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        fn();
      };

      let ws: SocketLike;
      try {
        ws = this.opts.createSocket(this.opts.url());
      } catch (e) {
        reject(socketError(e instanceof Error ? e.message : "Could not open the connection"));
        return;
      }
      this.ws = ws;

      const timeout = setTimeout(() => {
        settle(() => reject(socketError("Connection timed out")));
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      }, this.opts.connectTimeoutMs);

      ws.onopen = () => {
        if (this.ws !== ws) return;
        this.attempts = 0;
        this.send({
          type: "start",
          sessionId: this.sessionId,
          from: this.opts.fromLang,
          to: this.opts.toLang,
          mode: "auto-lid",
          sampleRate: 16000,
          clientVad: false,
        });
        if (this.paused) this.send({ type: "pause" });
        if (this.pingTimer) clearInterval(this.pingTimer);
        this.pingTimer = setInterval(
          () => this.send({ type: "ping", t: Date.now() }),
          this.opts.pingIntervalMs,
        );
        this.setState("connected");
        settle(resolve);
      };

      ws.onmessage = (ev) => {
        if (this.ws !== ws) return;
        this.handleMessage(ev.data);
      };

      ws.onerror = () => {
        // Always followed by onclose, which decides what happens next.
      };

      ws.onclose = () => {
        if (this.ws !== ws) return;
        this.ws = null;
        if (this.pingTimer) clearInterval(this.pingTimer);
        this.pingTimer = null;
        if (this.closing) {
          this.setState("closed");
          settle(() => reject(socketError("Connection closed")));
          return;
        }
        if (!settled) {
          // The first open failing is the caller's call (it cancels the
          // session); a failed REconnect just tries again.
          if (initial) this.setState("closed");
          settle(() => reject(socketError("Could not connect to the interpreter")));
          return;
        }
        this.scheduleReconnect();
      };
    });
  }

  private scheduleReconnect(): void {
    if (this.closing) return;
    if (this.attempts >= this.opts.maxReconnectAttempts) {
      this.closing = true;
      this.setState("closed");
      this.opts.events.onError?.(
        socketError("Lost the connection to the interpreter", "WS_LOST", false),
      );
      return;
    }
    this.attempts += 1;
    this.setState("reconnecting");
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.closing) return;
      this.open(false).catch(() => this.scheduleReconnect());
    }, this.opts.reconnectDelayMs);
  }

  private handleMessage(data: unknown): void {
    let msg: Record<string, unknown>;
    try {
      msg = JSON.parse(typeof data === "string" ? data : "") as Record<string, unknown>;
    } catch {
      return;
    }
    const ev = this.opts.events;
    switch (msg.type) {
      case "status":
        ev.onStatus?.(String(msg.status ?? ""));
        break;
      case "final":
        ev.onFinal?.({
          asr: typeof msg.asr === "string" ? msg.asr : "",
          mt: typeof msg.mt === "string" ? msg.mt : "",
          lid: typeof msg.lid === "string" ? msg.lid : undefined,
          speaker: typeof msg.speaker === "string" ? msg.speaker : undefined,
          audioUrl: typeof msg.audioUrl === "string" ? msg.audioUrl : null,
          audioB64: typeof msg.audioB64 === "string" ? msg.audioB64 : null,
          audioMime: typeof msg.audioMime === "string" ? msg.audioMime : null,
          objectName: typeof msg.objectName === "string" ? msg.objectName : null,
          playbackSec: typeof msg.playbackSec === "number" ? msg.playbackSec : undefined,
          e2eServerMs: e2eOf(msg.timing),
        });
        break;
      case "detected_speaker":
        if (msg.speaker === "A" || msg.speaker === "B") ev.onDetectedSpeaker?.(msg.speaker);
        break;
      case "noise_warning":
        ev.onNoiseWarning?.(!!msg.show);
        break;
      case "error": {
        const recoverable = msg.recoverable !== false;
        const err = socketError(
          String(msg.message || "Interpreter error"),
          typeof msg.code === "string" ? msg.code : undefined,
          recoverable,
        );
        if (!recoverable) {
          // The server will close us; do not reconnect into the same refusal.
          this.closing = true;
          this.clearTimers();
        }
        ev.onError?.(err);
        break;
      }
      default:
        break; // pong and anything newer
    }
  }
}
